/*
  About page motion (sections/about-intro.liquid, sections/about-timeline.liquid):
  reveals [data-reveal] and [data-step] elements as they scroll in, sizes the timeline spine
  from the first node to the last, and fills it to follow the reader.
  Both sections load this file, so it guards against running twice.
*/
(() => {
  if (window.emperAboutLoaded) return;
  window.emperAboutLoaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches || !('IntersectionObserver' in window)) return;

  const timelines = new Set();
  let ticking = false;

  const revealer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px' }
  );

  const resizer = 'ResizeObserver' in window ? new ResizeObserver(() => measure()) : null;

  // a node's centre in the timeline's own coordinates; offsets ignore the reveal transforms
  function nodeCentre(node, timeline) {
    let y = node.offsetHeight / 2;
    for (let el = node; el && el !== timeline; el = el.offsetParent) y += el.offsetTop;
    return y;
  }

  // the spine runs between the centres of the first and last nodes
  function measure() {
    timelines.forEach((timeline) => {
      const nodes = timeline.querySelectorAll('.about-step__node');
      if (!nodes.length) return;
      timeline.style.setProperty('--spine-top', `${nodeCentre(nodes[0], timeline)}px`);
      timeline.style.setProperty(
        '--spine-bottom',
        `${timeline.offsetHeight - nodeCentre(nodes[nodes.length - 1], timeline)}px`
      );
    });
    update();
  }

  function update() {
    ticking = false;
    const line = window.innerHeight * 0.6;
    timelines.forEach((timeline) => {
      if (!timeline.isConnected) {
        timelines.delete(timeline);
        resizer?.unobserve(timeline);
        return;
      }
      const spine = timeline.querySelector('.about-timeline__spine').getBoundingClientRect();
      const progress = Math.min(1, Math.max(0, (line - spine.top) / spine.height));
      timeline.style.setProperty('--tl-progress', progress.toFixed(4));
      timeline.querySelectorAll('[data-step]').forEach((step) => {
        const node = step.querySelector('.about-step__node').getBoundingClientRect();
        step.classList.toggle('is-active', node.top + node.height / 2 <= line);
      });
    });
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  function setup(scope) {
    scope.querySelectorAll('[data-about]:not(.about-anim)').forEach((root) => {
      root.classList.add('about-anim');
      root.querySelectorAll('[data-reveal], [data-step]').forEach((el) => revealer.observe(el));
      root.querySelectorAll('[data-timeline]').forEach((timeline) => {
        timelines.add(timeline);
        resizer?.observe(timeline);
      });
    });
    measure();
  }

  setup(document);
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', measure);
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();
